import { ApolloClient } from '@apollo/client';
import { assign, get, map } from 'lodash';
import logger from '../common/helpers/logger/logger';
import { OTHER_PREVIEW_QUERY } from '../queries/contentPage/otherPreview';
import {
  POST_DETAIL_QUERY,
  PostDetailQueryData,
  PostDetailQueryVars,
} from '../queries/contentPage/postDetail';
import { READ_NEXT_QUERY } from '../queries/contentPage/readNext';
import {
  ARTICLE_BLOCK_QUERY,
  ArticleBlockQueryResponse,
  ArticleBlockPost,
} from '../queries/homePage/articleBlock';
import {
  HERO_ARTICLES_QUERY,
  HeroArticleInfo,
  HeroArticlesQueryRequest,
  HeroArticlesQueryResponse,
} from '../queries/homePage/heroArticles';
import {
  HighlightItem,
  HIGHLIGHTS_QUERY,
  HighlightsQueryData,
} from '../queries/homePage/highlights';
import {
  MOST_POPULAR_QUERY,
  MostPopular,
  MostPopularQueryRequest,
  MostPopularQueryResponse,
} from '../queries/homePage/mostPopular';
import {
  POST_LIST_QUERY,
  PostListQueryData,
} from '../queries/homePage/postList';
import { useMutationHelper } from '../common/helpers/data/useMutationHelper';
import {
  COUNT_PAGE_VIEW_MUTATION,
  CountPageViewMutationResponse,
  CountPageViewMutationVars,
} from '../queries/contentPage/incrementView';
import {
  getPropertyTypeFromUrlSegment,
  PropertyType,
} from '../common/helpers/domain/propertyTypes';
import { PostSubcategory } from '../common/helpers/domain/postCategories';

interface FetchPostListIAOptions {
  propertyType?: PropertyType;
  termsTaxonomy?: string;
  termsSlug?: string;
  offset?: number;
  perPage: number;
  postCategory?: string[];
  videoType?: string[];
  exclude?: number[];
}

export async function fetchPostList(
  client: ApolloClient<unknown>,
  options: FetchPostListIAOptions,
): Promise<PostListQueryData['posts'] | undefined> {
  try {
    logger.logInfo('postService.fetchPostList', 'Begin', options);

    const variables = {
      propertyType: options.propertyType,
      termsTaxonomy: options.termsTaxonomy,
      termsSlug: options.termsSlug,
      offset: options.offset,
      perPage: options.perPage,
      postCategory: options.postCategory,
      videoType: options.videoType,
      exclude: options.exclude,
    };

    const response = await client.query<
      PostListQueryData,
      FetchPostListIAOptions
    >({
      query: POST_LIST_QUERY,
      variables,
    });
    return response?.data?.posts;
  } catch (error) {
    logger.logError(
      'postService.fetchPostList',
      'Error retrieving from query.',
      {
        options,
        error,
      },
    );
    throw error;
  }
}

interface FetchReadNextPostsOptions {
  offset: number;
  perPage: number;
  postId?: number;
}

export const fetchReadNextPosts = async (
  client: ApolloClient<unknown>,
  options: FetchReadNextPostsOptions,
) => {
  try {
    logger.logInfo('postService.fetchReadNextPosts', 'Begin', options);

    const variables = {
      offset: options.offset,
      perPage: options.perPage,
      id: options.postId,
    };

    const response = await client.query({
      query: READ_NEXT_QUERY,
      variables: variables,
    });
    return get(response, 'data', {});
  } catch (error) {
    logger.logError(
      'postService.fetchReadNextPosts',
      'Error retrieving from query.',
      {
        options,
        error,
      },
    );
    throw error;
  }
};

interface FetchHeroArticleOptions {
  propertyType?: PropertyType;
  postCategory?: PostSubcategory[];
  tag?: string;
}

export async function fetchHeroArticle(
  client: ApolloClient<unknown>,
  options: FetchHeroArticleOptions,
): Promise<HeroArticleInfo> {
  try {
    logger.logInfo('postService.fetchHeroArticle', 'Begin', options);

    const variables = {
      propertyType: options.propertyType,
      postCategory: options.postCategory,
      tag: options.tag,
    };

    const response = await client.query<
      HeroArticlesQueryResponse,
      HeroArticlesQueryRequest
    >({
      query: HERO_ARTICLES_QUERY,
      variables: variables,
    });

    return response?.data?.heroArticle;
  } catch (error) {
    logger.logError(
      'postService.fetchHeroArticle',
      'Error retrieving from query.',
      error,
    );
    throw error;
  }
}

interface FetchPopularArticlesOptions {
  propertyType?: PropertyType[];
  postCategory?: PostSubcategory[];
  tag?: string;
  popularFromDate: number;
  perPage: number;
}

export async function fetchMostPopularArticles(
  client: ApolloClient<unknown>,
  options: FetchPopularArticlesOptions,
): Promise<MostPopular[]> {
  try {
    logger.logInfo('postService.fetchMostPopularArticles', 'Begin', options);

    const response = await client.query<
      MostPopularQueryResponse,
      MostPopularQueryRequest
    >({
      query: MOST_POPULAR_QUERY,
      variables: options,
    });

    return response?.data?.mostPopular ?? [];
  } catch (error) {
    logger.logError(
      'postService.fetchMostPopularArticles',
      'Error retrieving from query.',
      {
        options,
        error,
      },
    );
    throw error;
  }
}

export async function fetchHighlights(
  client: ApolloClient<unknown>,
): Promise<HighlightItem[]> {
  try {
    logger.logInfo('postService.fetchHighlights', 'Begin');

    const result = await client.query<HighlightsQueryData>({
      query: HIGHLIGHTS_QUERY,
    });

    return result?.data?.highlights?.items ?? [];
  } catch (error) {
    logger.logError(
      'postService.fetchHighlights',
      'Error retrieving from query.',
      error,
    );
    throw error;
  }
}

export async function fetchArticleBlock(
  client: ApolloClient<unknown>,
): Promise<ArticleBlockPost[]> {
  try {
    logger.logInfo('postService.fetchArticleBlock', 'Begin');

    const result = await client.query<ArticleBlockQueryResponse>({
      query: ARTICLE_BLOCK_QUERY,
    });

    return result?.data?.articleBlock?.items ?? [];
  } catch (error) {
    logger.logError(
      'postService.fetchArticleBlock',
      'Error retrieving from query.',
      error,
    );
    throw error;
  }
}

export const fetchPostDetails = async (
  client: ApolloClient<unknown>,
  options: {
    articleId: number;
  },
): Promise<PostDetailQueryData> => {
  try {
    logger.logInfo('postService.fetchPostDetails', 'Begin', options);

    const variables = {
      id: options.articleId,
      errorPolicy: 'all',
    };

    const response = await client.query<
      PostDetailQueryData,
      PostDetailQueryVars
    >({
      query: POST_DETAIL_QUERY,
      variables: variables,
    });
    return get(response, 'data');
  } catch (error) {
    logger.logError(
      'postService.fetchPostDetails',
      'Error retrieving from query.',
      {
        options,
        error,
      },
    );
    throw error;
  }
};

interface FetchOtherPreviewPostsOptions {
  postId: number;
  propertyType: string[];
  postCategory?: PostSubcategory | PostSubcategory[];
  videoType?: [string];
  locationDistrictId?: number[];
}

export const fetchOtherPreviewPosts = async (
  client: ApolloClient<unknown>,
  options: FetchOtherPreviewPostsOptions,
) => {
  let variables;
  try {
    logger.logInfo('postService.fetchOtherPreviewPosts', 'Begin', options);

    const propertyTypeValue = map(options.propertyType, (item) => {
      const type = item.toLowerCase().replace(/\s/g, '');
      return getPropertyTypeFromUrlSegment(type);
    });

    variables = {
      exclude: [options.postId],
      propertyType: propertyTypeValue,
      locationDistrictId: options?.locationDistrictId,
    };
    variables = assign(variables, { postCategory: options.postCategory });
    const response = await client.query({
      query: OTHER_PREVIEW_QUERY,
      variables: variables,
    });

    return get(response, 'data', {});
  } catch (error) {
    logger.logError(
      'postService.fetchOtherPreviewPosts',
      'Error retrieving from query.',
      {
        variables,
        options,
        error,
      },
    );
    // TODO: Decide best way to return error, whether by throw or return empty
    //throw error;
    return {};
  }
};

export const useCountPostViewMutation = () => {
  return useMutationHelper<
    CountPageViewMutationResponse,
    CountPageViewMutationVars
  >(COUNT_PAGE_VIEW_MUTATION);
};
